/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { Injectable } from '@angular/core';
import { LbRbacService } from '../../modules/loadbalancer/shared';
import { LogService, NotifyService, RbacService } from '../services';
import { environment, ModulesEnum } from '../../../environments/environment';

@Injectable()
export class RbacPageGuard {
    constructor(
        private lbRbacService: LbRbacService,
        private logService: LogService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router
    ) {
    }

    /**
     * Get the Modules ENUM, so we can use it in the view.
     */
    get modulesEnum(): any {
        return ModulesEnum;
    }

    /**
     * Can activate route.
     * @param route
     * @param state
     */
    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): any {
        const url: string = state.url;
        const baseUrl: string = this.stripParamsFromUrl(route, url);
        if (baseUrl.includes('?') || baseUrl.includes('&') || baseUrl.includes('=')) {
            this.logService.error('RbacService:getPagePermission', 'baseUrl contains unexpected characters');
            this.router.navigateByUrl('/404').then().catch();
            this.notifyService.error('The url you are trying to navigate to contains unexpected characters.');
        } else {
            const lookupUrl: string = environment.module === this.modulesEnum.LOADBALANCER
                ? this.lbRbacService.stripApplianceIdFromUrl(baseUrl)
                : baseUrl;
            const hasPermission: boolean = this.rbacService.getPagePermission(lookupUrl);
            if (!hasPermission) {
                const baseUrl: string = this.getBaseUrl(route, url) + '/no-access';
                this.router.navigateByUrl(baseUrl, { state: { url: url.toString() } }).then().catch();
            }

            return hasPermission;
        }
    }

    /**
     * Strip parameters from URL.
     * @param route
     * @param url
     * @private
     */
    private stripParamsFromUrl(route: ActivatedRouteSnapshot, url: string): string {
        let baseUrl: string = url;
        const params: any[] = Object.values(route.params);
        for (const param of params) {
            const excludedParams: string[] = this.lbRbacService.getExcludedParams();
            if (excludedParams?.includes(param)) {
                this.logService.debug('RbacService:stripParamsFromUrl', 'URL is in excluded Params', param);
            } else {
                baseUrl = baseUrl.replace('/' + param, '');
            }
        }

        // Remove query string from the url
        baseUrl = baseUrl.split('?')[0];

        return baseUrl;
    }

    /**
     * Get base url.
     * @param route
     * @param url
     * @private
     */
    private getBaseUrl(route: ActivatedRouteSnapshot, url: string): string {
        const regex: RegExp = new RegExp(route.url.map((segment: UrlSegment): string => `/${segment}`).join(''), 'ig');

        return url.replace(regex, '');
    }
}
